import { useCallback, useMemo, memo, useEffect } from 'react';
import TableColumnsProvider from 'framework-listing-lib/internal/Table/providers/TableColumnsProvider';
import usePerformanceTracking, { useSyncPerformanceTracking } from 'framework-listing-lib/internal/hooks/usePerformanceTracking';
import { MEASURE_MARK_NAMES } from 'framework-listing-lib/internal/utils/performanceTracking';
import useFetchCrmObjectType from 'framework-listing-lib/hooks/useFetchCrmObjectType';
import useCrmObjectTypeProperties from 'framework-listing-lib/internal/hooks/useCrmObjectTypeProperties';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import useIsLoading from 'framework-listing-lib/internal/hooks/useIsLoading';
import ListingLibFrameworkDataTable from 'framework-listing-lib/internal/FrameworkDataTable/components/ListingLibFrameworkDataTable';
import AsyncCrmTableErrorState from './AsyncCrmTableErrorState';
import ListingLibBulkActionsProvider from '../providers/ListingLibBulkActionsProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function FrameworkTable({
  actionsComponent,
  bulkActionProps,
  columnKeys,
  maxHeight,
  pageSizes,
  renderCustomColumn,
  renderEmptyState = () => null,
  renderErrorMarker = () => null,
  renderNestedContent,
  renderPqlRow,
  renderSuccessMarker = () => null,
  rowProps,
  withInlineEditCellTour = true
}) {
  const performanceTracking = usePerformanceTracking(MEASURE_MARK_NAMES.LIFECYCLE);
  const {
    trackPerformanceInteraction
  } = useCrmObjectTypeTracking();
  const {
    data,
    error
  } = useFetchCrmObjectType();
  const {
    dataAsJS: propertyMap
  } = useCrmObjectTypeProperties();
  const isLoading = useIsLoading();
  const validColumnKeys = useMemo(() => {
    if (isLoading && !columnKeys) {
      return [];
    }
    const columnsArr = columnKeys !== null && columnKeys !== void 0 && columnKeys.length ? columnKeys : Object.keys(propertyMap).map(key => propertyMap[key].name);

    /**
     * Use only visible properties defined in @propertyMap
     */
    return columnsArr.filter(col => col in propertyMap);
  }, [columnKeys, isLoading, propertyMap]);
  const isError = useMemo(() => {
    // Handle partial data from GraphQL
    if (data) {
      return false;
    }
    return Boolean(error);
  }, [data, error]);
  const isSuccessMarker = useMemo(() => Boolean(!isLoading && !isError && renderSuccessMarker), [isError, isLoading, renderSuccessMarker]);
  const isErrorMarker = useMemo(() => Boolean(isError && renderErrorMarker), [isError, renderErrorMarker]);
  const renderError = useCallback(() => {
    return renderErrorMarker(error);
  }, [renderErrorMarker, error]);
  const renderSuccess = useCallback(() => {
    return renderSuccessMarker();
  }, [renderSuccessMarker]);
  useEffect(() => {
    if (isLoading) {
      return;
    }
    performanceTracking.markTimeEnd();
  }, [isLoading, performanceTracking, trackPerformanceInteraction]);
  useSyncPerformanceTracking();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isSuccessMarker && renderSuccess(), isErrorMarker && renderError(), isError && /*#__PURE__*/_jsx(AsyncCrmTableErrorState, {}), !isError && /*#__PURE__*/_jsx(ListingLibBulkActionsProvider, {
      bulkActionProps: bulkActionProps,
      data: data,
      rowProps: rowProps,
      children: /*#__PURE__*/_jsx(TableColumnsProvider, {
        columnKeys: validColumnKeys,
        propertyMap: propertyMap,
        renderCustomColumn: renderCustomColumn,
        total: data ? data.total : 0,
        children: /*#__PURE__*/_jsx(ListingLibFrameworkDataTable, {
          actionsComponent: actionsComponent,
          bulkActionProps: bulkActionProps,
          data: data,
          loading: isLoading,
          maxHeight: maxHeight,
          pageSizes: pageSizes,
          renderEmptyState: renderEmptyState,
          renderNestedContent: renderNestedContent,
          renderPqlRow: renderPqlRow,
          rowProps: rowProps,
          withInlineEditCellTour: withInlineEditCellTour
        })
      })
    })]
  });
}
export default /*#__PURE__*/memo(FrameworkTable);